// ABOUTME: Toggle switch component built on Radix UI Switch primitive
// ABOUTME: Provides Flowbite-compatible API with label support

import * as React from "react"
import * as SwitchPrimitives from "@radix-ui/react-switch"
import * as LabelPrimitive from "@radix-ui/react-label"
import { cn } from "@shared/lib/utils"

export interface CToggleSwitchProps extends Omit<
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
  "onCheckedChange" | "onChange"
> {
  onChange?: (checked: boolean) => void
  label?: string
  color?: "default" | "success" | "failure"
}

export const CToggleSwitch = React.forwardRef<React.ComponentRef<typeof SwitchPrimitives.Root>, CToggleSwitchProps>(
  ({ className, onChange, checked, label, color, id, ...props }, ref) => {
    const generatedId = React.useId()
    const switchId = id || generatedId

    const colorClasses = {
      default: "",
      success: "data-[state=checked]:bg-green-500",
      failure: "data-[state=checked]:bg-red-500",
    }

    const handleCheckedChange = (checkedState: boolean) => {
      if (onChange) {
        onChange(checkedState)
      }
    }

    return (
      <div className={cn("flex items-center gap-2", !label && "mr-2")}>
        <SwitchPrimitives.Root
          ref={ref}
          id={switchId}
          checked={checked}
          onCheckedChange={handleCheckedChange}
          className={cn(
            "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200",
            color && colorClasses[color],
            className,
          )}
          {...props}
        >
          <SwitchPrimitives.Thumb
            className={cn(
              "pointer-events-none block h-4 w-4 rounded-full bg-primary-foreground shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0",
            )}
          />
        </SwitchPrimitives.Root>
        {label && (
          <LabelPrimitive.Root
            htmlFor={switchId}
            className="text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
          </LabelPrimitive.Root>
        )}
      </div>
    )
  },
)

CToggleSwitch.displayName = "CToggleSwitch"
